import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdPerson, MdSearch, MdExpandMore, MdExpandLess, MdChevronLeft, MdChevronRight, MdSecurity, MdWarning, MdFilterList } from 'react-icons/md';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [mfaResetLoading, setMfaResetLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const USERS_PER_PAGE = 50;

  const loadUsers = useCallback(async (page = 1, search = '', accountType = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: USERS_PER_PAGE.toString()
      });
      if (search) {
        params.append('search', search);
      }
      if (accountType) {
        params.append('account_type', accountType);
      }
      const response = await axios.get(`${API_URL}/api/admin/users?${params}`);
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.page);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(1, '', '');
  }, [loadUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
    loadUsers(1, searchInput, accountTypeFilter);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
    loadUsers(1, '', accountTypeFilter);
  };

  const handleAccountTypeChange = (type) => {
    setAccountTypeFilter(type);
    setCurrentPage(1);
    setExpandedUser(null);
    loadUsers(1, searchTerm, type);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadUsers(newPage, searchTerm, accountTypeFilter);
      setExpandedUser(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const toggleExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const handleResetUserMfa = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to reset MFA for user "${username}"? They will need to re-enroll in MFA.`)) {
      return;
    }
    
    try {
      setMfaResetLoading(userId);
      await axios.post(`${API_URL}/api/admin/reset-user-mfa`, { user_id: userId });
      setMessage({ type: 'success', text: `MFA has been reset for ${username}. They have been notified.` });
      // Refresh user data
      loadUsers(currentPage, searchTerm, accountTypeFilter);
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      console.error('Failed to reset MFA:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to reset MFA' });
    } finally {
      setMfaResetLoading(null);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Message Display */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${
          message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <MdSecurity className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <MdWarning className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          )}
          <p className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>{message.text}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 font-rubik flex items-center gap-2">
            <MdPerson className="text-primary" />
            User Management
          </h2>
          <p className="text-gray-600 font-rubik text-sm mt-1">
            {totalUsers.toLocaleString()} {accountTypeFilter ? accountTypeFilter + 's' : 'registered user' + (totalUsers !== 1 ? 's' : '')}
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="relative w-full md:w-96 flex gap-2">
          <div className="relative flex-1">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by username..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg font-rubik text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="user-search-input"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-600 font-rubik text-sm"
            data-testid="search-button"
          >
            Search
          </button>
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-rubik text-sm"
              data-testid="clear-search-button"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Account Type Filter */}
      <div className="flex items-center gap-2 mb-4" data-testid="account-type-filter">
        <MdFilterList className="text-gray-500" />
        <span className="text-sm text-gray-500 font-rubik">Filter:</span>
        <button
          onClick={() => handleAccountTypeChange('')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium font-rubik transition-colors ${
            accountTypeFilter === '' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="filter-all"
        >
          All Users
        </button>
        <button
          onClick={() => handleAccountTypeChange('voter')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium font-rubik transition-colors ${
            accountTypeFilter === 'voter' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="filter-voters"
        >
          Voters
        </button>
        <button
          onClick={() => handleAccountTypeChange('representative')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium font-rubik transition-colors ${
            accountTypeFilter === 'representative' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
          }`}
          data-testid="filter-representatives"
        >
          Representatives
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="card-container text-center py-12">
          <MdPerson className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-rubik">
            {searchTerm ? 'No users found matching your search' : 'No users registered yet'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-card shadow-card overflow-hidden">
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Responses
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Points
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Custom Polls
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map(user => (
                    <React.Fragment key={user.userId}>
                      <tr className="hover:bg-gray-50" data-testid={`user-row-${user.userId}`}>
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-gray-800 font-rubik">{user.username}</p>
                            <p className="text-xs text-gray-500 font-rubik">{user.email || 'No email'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {user.accountType === 'representative' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-testid={`user-status-${user.userId}`}>
                              Representative
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-testid={`user-status-${user.userId}`}>
                              Voter
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 font-rubik">
                          {user.city && user.city !== 'N/A'
                            ? `${user.city}, ${user.state || user.country}`
                            : user.state && user.state !== 'N/A' 
                              ? `${user.state}, ${user.country}` 
                              : user.country || 'N/A'}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {user.totalResponses.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            {user.pollPoints.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                            {user.customPolls.total}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 font-rubik">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => toggleExpand(user.userId)}
                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                            data-testid={`expand-user-${user.userId}`}
                          >
                            {expandedUser === user.userId ? (
                              <MdExpandLess className="text-xl text-gray-600" />
                            ) : (
                              <MdExpandMore className="text-xl text-gray-600" />
                            )}
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Details */}
                      {expandedUser === user.userId && (
                        <tr className="bg-gray-50">
                          <td colSpan={8} className="px-4 py-4">
                            <div className="grid md:grid-cols-3 gap-6">
                              {/* Profile Info */}
                              <div>
                                <h4 className="font-semibold text-gray-700 font-rubik mb-2">Profile Information</h4>
                                <div className="space-y-1 text-sm">
                                  <p><span className="text-gray-500">Birthdate:</span> <span className="text-gray-800">{formatDate(user.birthdate) || 'Not set'}</span></p>
                                  <p><span className="text-gray-500">Gender:</span> <span className="text-gray-800 capitalize">{user.gender || 'Not set'}</span></p>
                                  <p><span className="text-gray-500">Ethnicity:</span> <span className="text-gray-800">{user.ethnicity || 'Not set'}</span></p>
                                  <p><span className="text-gray-500">Education:</span> <span className="text-gray-800">{user.education || 'Not set'}</span></p>
                                  <p><span className="text-gray-500">Military Service:</span> <span className="text-gray-800" data-testid={`user-military-${user.userId}`}>{user.militaryService || 'Not set'}</span></p>
                                  <p><span className="text-gray-500">City:</span> <span className="text-gray-800">{user.city || 'Not set'}</span></p>
                                  <p><span className="text-gray-500">Zip Code:</span> <span className="text-gray-800">{user.zipCode || 'N/A'}</span></p>
                                </div>
                              </div>
                              
                              {/* Response Stats */}
                              <div>
                                <h4 className="font-semibold text-gray-700 font-rubik mb-2">Response Statistics</h4>
                                <div className="space-y-1 text-sm">
                                  <p><span className="text-gray-500">System Poll Responses:</span> <span className="text-gray-800 font-medium">{user.systemPollResponses.toLocaleString()}</span></p>
                                  <p><span className="text-gray-500">Custom Poll Responses:</span> <span className="text-gray-800 font-medium">{user.customPollResponses.toLocaleString()}</span></p>
                                  <p><span className="text-gray-500">Total Responses:</span> <span className="text-gray-800 font-medium">{user.totalResponses.toLocaleString()}</span></p>
                                  <p><span className="text-gray-500">Poll Points:</span> <span className="text-green-600 font-medium">{user.pollPoints.toLocaleString()}</span></p>
                                </div>
                              </div>
                              
                              {/* Custom Polls by Status */}
                              <div>
                                <h4 className="font-semibold text-gray-700 font-rubik mb-2">Custom Polls by Status</h4>
                                <div className="flex flex-wrap gap-2">
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Pending: {user.customPolls.pending}
                                  </span>
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Active: {user.customPolls.active}
                                  </span>
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Closed: {user.customPolls.closed}
                                  </span>
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Rejected: {user.customPolls.rejected}
                                  </span>
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    Flagged: {user.customPolls.flagged}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500 mt-2">
                                  Total Custom Polls: <span className="font-medium text-gray-800">{user.customPolls.total}</span>
                                </p>
                                
                                {/* MFA Status & Reset */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <h4 className="font-semibold text-gray-700 font-rubik mb-2 flex items-center gap-2">
                                    <MdSecurity className="text-gray-500" />
                                    Security
                                  </h4>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-500">MFA Status:</span>
                                      {user.mfaEnabled ? (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Enabled
                                        </span>
                                      ) : (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                          Not Enabled
                                        </span>
                                      )}
                                    </div>
                                    {user.mfaEnabled && (
                                      <button
                                        onClick={() => handleResetUserMfa(user.userId, user.username)}
                                        disabled={mfaResetLoading === user.userId}
                                        className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors flex items-center gap-1 disabled:opacity-50"
                                        data-testid={`reset-mfa-${user.userId}`}
                                      >
                                        {mfaResetLoading === user.userId ? (
                                          <>
                                            <div className="animate-spin rounded-full h-3 w-3 border border-red-600 border-t-transparent"></div>
                                            Resetting...
                                          </>
                                        ) : (
                                          'Reset MFA'
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-600 font-rubik">
                Showing {((currentPage - 1) * USERS_PER_PAGE) + 1} - {Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers.toLocaleString()} users
              </p>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
                  data-testid="prev-page-button"
                >
                  <MdChevronLeft className="text-xl" />
                </button>
                
                {getPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === 'number' && handlePageChange(page)}
                    disabled={page === '...'}
                    className={`px-3 py-1 rounded-lg font-rubik text-sm ${
                      page === currentPage 
                        ? 'bg-primary text-white' 
                        : page === '...' 
                          ? 'text-gray-400 cursor-default'
                          : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`page-${page}`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
                  data-testid="next-page-button"
                >
                  <MdChevronRight className="text-xl" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminUsers;
